import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  LinearProgress,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { getPriceSyncStatus, triggerPriceSync, PriceSyncStatus } from '../services/api';
import { bloomberg } from '../theme/bloombergTheme';

const PriceSyncStatusBar: React.FC = () => {
  const [status, setStatus] = useState<PriceSyncStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' });
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const data = await getPriceSyncStatus();
      setStatus(data);
      if (data.sync_running) setSyncing(true);
    } catch (error) {
      console.error('Error fetching price sync status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncNow = async () => {
    try {
      setSyncing(true);
      const result = await triggerPriceSync();
      setSnackbar({ open: true, message: result.message || 'Sync started.', severity: 'info' });
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(fetchStatus, 5000);
      fetchStatus();
    } catch (error: unknown) {
      const msg = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : 'Failed to start sync';
      setSnackbar({ open: true, message: String(msg), severity: 'error' });
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => {
      clearInterval(interval);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    if (status && !status.sync_running && syncing) {
      setSyncing(false);
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      fetchStatus();
    }
  }, [status?.sync_running, syncing]);

  const formatTimestamp = (timestamp: string | null): string => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  if (!status && loading) return null;
  if (!status) return null;

  const tooltipContent = (
    <Box sx={{ p: 0.5, minWidth: 220 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', mb: 1 }}>Price Sync Status</Typography>

      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)' }}>Total Synced</Typography>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
            {status.stocks_with_price.toLocaleString()} / {status.total_stocks.toLocaleString()} ({status.sync_percentage}%)
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={status.sync_percentage}
          sx={{ height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { borderRadius: 2, bgcolor: '#22c55e' } }}
        />
      </Box>

      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)' }}>Recent (24h)</Typography>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
            {status.stocks_with_recent_price.toLocaleString()} ({status.recent_sync_percentage}%)
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={status.recent_sync_percentage}
          sx={{ height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { borderRadius: 2, bgcolor: bloomberg.accent.orange } }}
        />
      </Box>

      {status.stale_count > 0 && (
        <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', mt: 0.75 }}>
          {status.stale_count.toLocaleString()} stocks with stale prices
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <Tooltip title={tooltipContent} arrow placement="bottom-start" slotProps={{ tooltip: { sx: { bgcolor: '#171a21', border: '1px solid #334155', maxWidth: 280 } } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'default' }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: status.recent_sync_percentage > 50 ? '#22c55e' : status.recent_sync_percentage > 0 ? bloomberg.accent.orange : '#ef4444' }} />
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
            Updated {formatTimestamp(status.latest_price_timestamp)}
          </Typography>
        </Box>
      </Tooltip>

      <Button
        size="small"
        variant="outlined"
        startIcon={syncing ? <CircularProgress size={12} /> : <SyncIcon sx={{ fontSize: 14 }} />}
        onClick={handleSyncNow}
        disabled={loading || syncing}
        sx={{ fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 0, height: 24, textTransform: 'none' }}
      >
        {syncing ? 'Syncing...' : 'Sync Now'}
      </Button>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PriceSyncStatusBar;
